/**
 * Shared UI component for UI Button.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import { UiButtonSize, UiButtonType, UiButtonVariant } from './ui-button.types';
import { resolveTranslatedUiLabel } from '../ui-label-translate.util';

/**
 * COMPONENT: Ui Button
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-button',
  standalone: true,
  templateUrl: './ui-button.component.html',
  styleUrls: ['./ui-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiButtonComponent implements AfterViewChecked {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly variant = input<UiButtonVariant>('primary');
  readonly size = input<UiButtonSize>('md');
  readonly type = input<UiButtonType>('button');
  /** PARENT-CHILD CONTRACT: disabled documents the input state boundary used by this file. */
  readonly disabled = input(false);
  /** PARENT-CHILD CONTRACT: fullWidth documents the input state boundary used by this file. */
  readonly fullWidth = input(false);
  /** PARENT-CHILD CONTRACT: ariaLabel documents the input state boundary used by this file. */
  readonly ariaLabel = input('');
  /** PARENT-CHILD CONTRACT: badgeText documents the input state boundary used by this file. */
  readonly badgeText = input('');
  /** PARENT-CHILD CONTRACT: icon documents the input state boundary used by this file. */
  readonly icon = input('');
  readonly iconPosition = input<'start' | 'end'>('start');
  /** PARENT-CHILD CONTRACT: label documents the input state boundary used by this file. */
  readonly label = input('');

  /** Bumped on every language change so the translated label recomputes. */
  private readonly langVersion = signal(0);
  /** Common raw projected labels that the wrapper can safely own and translate. */
  private readonly projectedSource = signal('');

  /**
   * Reactively resolved label. Recomputes when the source text changes or the
   * active language changes — no DOM mutation, so OnPush + content projection
   * both update live on language switch.
   */
  readonly translatedLabel = computed(() => {
    this.langVersion();
    const source = this.label().trim();
    return source ? resolveTranslatedUiLabel(this.translate, source) : '';
  });

  /** STATE OWNER: translatedProjectedLabel documents the computed state boundary used by this file. */
  readonly translatedProjectedLabel = computed(() => {
    this.langVersion();
    const source = this.projectedSource();
    return source ? resolveTranslatedUiLabel(this.translate, source) : '';
  });

  /** STATE OWNER: translatedAriaLabel documents the computed state boundary used by this file. */
  readonly translatedAriaLabel = computed(() => {
    this.langVersion();
    const source = this.ariaLabel().trim();
    return source ? resolveTranslatedUiLabel(this.translate, source) : '';
  });

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.langVersion.update((version) => version + 1));
  }

  /**
   * BUSINESS LOGIC: ngAfterViewChecked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  ngAfterViewChecked(): void {
    if (this.label().trim()) {
      return;
    }

    const label = this.host.nativeElement.querySelector('.ui-button__label');
    const projected = label?.textContent?.trim() ?? '';

    if (
      projected
      && label?.childElementCount === 0
      && projected !== this.projectedSource()
      && projected !== this.translatedProjectedLabel()
      && this.isSupportedRawProjection(projected)
    ) {
      this.projectedSource.set(projected);
      this.cdr.markForCheck();
    }
  }

  /**
   * BUSINESS LOGIC: isSupportedRawProjection
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private isSupportedRawProjection(value: string): boolean {
    return [
      'apply filters',
      'back',
      'cancel',
      'close',
      'create',
      'delete',
      'export',
      'info',
      'next',
      'previous',
      'reset',
      'retry',
      'save',
      'success',
      'update'
    ].includes(value.trim().toLowerCase());
  }
}
